import { createContext, useContext, useEffect, useState } from 'react';
import { MOCK, supabase, supabasePublic, T } from './supabase.js';

// Auth model เดียวกับ SourcePoint: Supabase email/password + role ต่อแอพ
// role มาจาก inbound.v_my_role (inbound.app_role → fallback map จาก public.profiles)
const AuthCtx = createContext(null);

const MOCK_ME = { role: 'admin', is_admin: true, can_see_cost: true };

export function AuthProvider({ children }) {
  const [session, setSession] = useState(MOCK ? { mock: true } : undefined); // undefined = loading
  const [me, setMe] = useState(MOCK ? MOCK_ME : null);
  const [recovery, setRecovery] = useState(false);   // มาจากลิงก์ "ลืมรหัสผ่าน" → ให้ตั้งรหัสใหม่

  useEffect(() => {
    if (MOCK) return;
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((e, s) => {
      setSession(s);
      if (e === 'PASSWORD_RECOVERY') setRecovery(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (MOCK || !session?.user) { if (!MOCK) setMe(null); return; }
    // เปลี่ยนคน (ไม่ผ่านการออกจากระบบ เช่น ลิงก์ลืมรหัสผ่านของอีกบัญชีในแท็บอื่น) → ล้างสิทธิ์ของคนเก่าก่อน
    //   ไม่งั้นระหว่างรอ สิทธิ์แอดมิน/ดูต้นทุนของคนเก่ายังนับว่า "โหลดแล้ว" (App เปิดหน้าตาม URL ด้วยค่านี้)
    //   + คำตอบของคนเก่าที่มาช้าห้ามเขียนทับของคนใหม่
    let alive = true;
    setMe(null);
    (async () => {
      const { data, error } = await supabase.from(T('v_my_role')).select('*').single();
      if (!alive) return;
      if (data) return setMe(data);
      // inbound ยังไม่ expose / bridge ยังไม่มา → ลอง shim public.inbound_my_role
      const { data: shim, error: shimErr } = await supabasePublic.rpc('inbound_my_role');
      if (!alive) return;
      if (shim?.role) return setMe(shim);
      setMe({
        role: 'none', is_admin: false, can_see_cost: false,
        dbPending: error?.code === 'PGRST106' && !!shimErr,
      });
    })();
    return () => { alive = false; };
  }, [session?.user?.id]);

  const signIn = (email, password) => supabasePublic.auth.signInWithPassword({ email, password });
  const signOut = () => supabase.auth.signOut();
  // ส่งอีเมลลิงก์ตั้งรหัสใหม่ กลับมาที่แอพนี้ (โดเมนต้องอยู่ใน Supabase Auth → Redirect URLs)
  const resetPassword = (email) =>
    supabasePublic.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
  // ตั้งรหัสใหม่ + ปลดธง "ต้องเปลี่ยนรหัสก่อนใช้งาน" ที่แอดมินติดไว้ตอน reset ให้ (deploy-35)
  // data: ... = user_metadata แบบผสม (ไม่ทับของเดิม เช่น full_name)
  const updatePassword = async (newPw) => {
    const r = await supabase.auth.updateUser({ password: newPw, data: { must_change_pw: false } });
    if (!r.error) { setRecovery(false); await supabase.auth.refreshSession().catch(() => {}); }
    return r;
  };

  return (
    <AuthCtx.Provider value={{ session, me, signIn, signOut, resetPassword, updatePassword, recovery, mock: MOCK }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
